import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  ForwardedRef,
} from "react";
import Fuse from "fuse.js";

import { Dropdown } from "Components/atoms/Dropdown/Desktop";
import { InputField } from "Components/atoms/InputField/Desktop";
import {
  FieldSearch,
  FieldSearchContent,
  FieldSearchDropdown,
} from "../styles";
import { Airport } from "shared/types/airportsTypes";
import { OriginFieldSearchProps } from "./types";
// @ts-ignore
import { ReactComponent as PlaneGoingSvg } from "assets/svgs/plane-going.svg";
import { removeAccents } from "shared/utils/removeAccents";
import airportsData from "shared/config/airports/AllAirports/airports.json";

/**
 * Componente OriginFieldSearch (Desktop)
 *
 * Barra de busca para selecionar origens no layout Desktop.
 */
export const OriginFieldSearch = forwardRef<
  HTMLDivElement,
  OriginFieldSearchProps
>(
  (
    {
      origins,
      isVisible,
      setIsVisible,
      onSelect,
      origin,
      fullWidthDropdown,
      variant,
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [filteredOrigins, setFilteredOrigins] = useState<Airport[]>(origins);
    const [isSearchTermTyped, setSearchTermTyped] = useState<boolean>(false);
    const [query, setQuery] = useState("");

    const fuse = new Fuse(airportsData, {
      keys: ["city_name", "airport_name", "airport_code"],
      threshold: 0.3,
      includeScore: true,
    });

    const handleAutoComplete = (value: string) => {
      setQuery(value);
      if (value.trim().length > 0) {
        const searchTerm = removeAccents(value).toLowerCase();
        const results = fuse.search(searchTerm);

        const formattedResults: Airport[] = results.map((result) => ({
          name: result.item.city_name,
          code: result.item.airport_code,
          description: `${result.item.city_name} - ${result.item.airport_name}, ${result.item.country_name}`,
          destak: result.item.is_metropolitan_area === 1,
        }));

        const uniqueResults = formattedResults.filter(
          (obj, index, self) =>
            self.findIndex((o) => o.code === obj.code) === index,
        );

        setFilteredOrigins(uniqueResults);
      } else {
        resetOrigins();
      }
    };

    const resetOrigins = () => {
      setFilteredOrigins([...origins]);
    };

    useEffect(() => {
      setQuery(origin);
    }, [origin]);

    const originRef = useRef<HTMLInputElement>(null);

    return (
      <FieldSearchContent ref={ref} $variant={variant}>
        <FieldSearch $variant={variant}>
          <PlaneGoingSvg />
          <InputField
            value={query}
            onFocus={() => {
              setIsVisible(true);
              originRef.current?.select();
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setIsVisible(true);
              handleAutoComplete(e.target.value);
              setSearchTermTyped(e.target.value.length > 0);
            }}
            onClear={() => {
              onSelect({ code: "", name: "", description: "" } as Airport);
              setQuery("");
              resetOrigins();
              setSearchTermTyped(false);
              setIsVisible(true);
              originRef.current?.select();
            }}
            placeholder="Partindo de onde?"
            aria-label="Campo de busca de origem"
            ref={originRef}
          />
        </FieldSearch>

        <FieldSearchDropdown
          type="origin"
          $fullWidth={fullWidthDropdown}
          $variant={variant}
        >
          {isVisible && (
            <Dropdown
              isVisible={isVisible}
              onSelect={(selected) => {
                onSelect(selected as Airport);
                setQuery(selected.description);
              }}
              suggestions={{
                title: "Origens mais buscadas",
                type: "origin",
                nationals: filteredOrigins,
                onSelect,
              }}
              isSearchTermTyped={isSearchTermTyped}
            />
          )}
        </FieldSearchDropdown>
      </FieldSearchContent>
    );
  },
);

OriginFieldSearch.displayName = "OriginFieldSearch";
